// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Card, Divider, Flex, Grid, Menu, Select, Text, TextInput, Title } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { getReferenceString, HTTP_HL7_ORG } from '@medplum/core';
import type {
  ChargeItem,
  Claim,
  ClaimDiagnosis,
  Condition,
  Coverage,
  Encounter,
  Patient,
  Practitioner,
} from '@medplum/fhirtypes';
import { DateTimeInput, useMedplum } from '@medplum/react';
import { resolvePayerClass, resolvePrice } from '@polarissleep/shared';
import { IconDownload, IconFileText, IconSend } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SAVE_TIMEOUT_MS } from '../../config/constants';
import { APPOINTMENT_TYPE_SYSTEM } from '../../constants/appointmentTypes';
import { useAppointmentTypes } from '../../hooks/useAppointmentTypes';
import { useDebouncedUpdateResource } from '../../hooks/useDebouncedUpdateResource';
import {
  applyChargeItemDefinition,
  calculateTotalPrice,
  findActiveChargeItemDefinitionByCpt,
} from '../../utils/chargeitems';
import { getCptChargeItems } from '../../utils/claims';
import { createSelfPayCoverage } from '../../utils/coverage';
import { getEmCptOptions } from '../../utils/em-cpt';
import { showErrorNotification } from '../../utils/notifications';
import { getIdentifier, NPI_SYSTEM } from '../../utils/practitioner';
import { ChargeItemList } from '../ChargeItem/ChargeItemList';

const CHARGE_ITEM_MODIFIER_URL = `${HTTP_HL7_ORG}/fhir/StructureDefinition/chargeitem-modifier`;

interface BillingTabProps {
  patient: Patient;
  encounter: Encounter;
  setEncounter: (encounter: Encounter) => void;
  practitioner: Practitioner | undefined;
  setPractitioner: (practitioner: Practitioner) => void;
  chargeItems: ChargeItem[] | undefined;
  setChargeItems: (chargeItems: ChargeItem[]) => void;
  claim: Claim | undefined;
  setClaim: (claim: Claim) => void;
  isLocked?: boolean;
}

function getEncounterDuration(encounter: Encounter): string {
  return getDurationFromPeriod(encounter.period?.start, encounter.period?.end);
}

function getDurationFromPeriod(startValue: string | undefined, endValue: string | undefined): string {
  const start = startValue ? new Date(startValue).getTime() : Number.NaN;
  const end = endValue ? new Date(endValue).getTime() : Number.NaN;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return 'N/A';
  }
  return String(Math.round((end - start) / 60000));
}

function isAppointmentChargeItem(chargeItem: ChargeItem): boolean {
  return (
    chargeItem.meta?.tag?.some(
      (tag) => tag.system === 'http://polarissleep.com/charge-item-source' && tag.code === 'auto-created'
    ) ?? false
  );
}

export const BillingTab = (props: BillingTabProps): JSX.Element => {
  const {
    encounter,
    setEncounter,
    claim,
    patient,
    practitioner,
    setPractitioner,
    chargeItems,
    setChargeItems,
    setClaim,
    isLocked = false,
  } = props;
  const medplum = useMedplum();
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [coverage, setCoverage] = useState<Coverage | undefined>();
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [npi, setNpi] = useState(practitioner ? getIdentifier(practitioner, NPI_SYSTEM) : '');
  const initialPrimaryChargeItem = chargeItems?.[0];
  const [primaryService, setPrimaryService] = useState(
    initialPrimaryChargeItem?.code?.text ?? initialPrimaryChargeItem?.code?.coding?.[0]?.display ?? ''
  );
  const [primaryBillingCode, setPrimaryBillingCode] = useState(
    initialPrimaryChargeItem?.code?.coding?.find((item) => item.code)?.code ?? ''
  );
  const [primaryDuration, setPrimaryDuration] = useState(getEncounterDuration(encounter));
  const [fallbackPrimaryPrice, setFallbackPrimaryPrice] = useState(0);
  const [primaryModifier, setPrimaryModifier] = useState(
    initialPrimaryChargeItem?.extension?.find((extension) => extension.url === CHARGE_ITEM_MODIFIER_URL)
      ?.valueCodeableConcept?.coding?.[0]?.code ?? ''
  );
  const debouncedUpdateResource = useDebouncedUpdateResource(medplum);

  const practitionerOptions = useMemo(() => {
    const options = practitioners.map((item) => ({
      value: item.id ?? '',
      label: `${item.name?.[0]?.given?.join(' ') ?? ''} ${item.name?.[0]?.family ?? ''}`.trim() || 'Practitioner',
    }));
    if (practitioner?.id && !options.some((option) => option.value === practitioner.id)) {
      options.unshift({
        value: practitioner.id,
        label:
          `${practitioner.name?.[0]?.given?.join(' ') ?? ''} ${practitioner.name?.[0]?.family ?? ''}`.trim() ||
          'Practitioner',
      });
    }
    return options;
  }, [practitioner, practitioners]);

  useEffect(() => {
    const fetchCoverage = async (): Promise<void> => {
      if (!patient) {
        return;
      }
      const coverageResult = await medplum.searchResources('Coverage', `patient=${getReferenceString(patient)}`);
      if (coverageResult.length > 0) {
        setCoverage(coverageResult[0] as Coverage);
      }
    };
    fetchCoverage().catch((err) => showErrorNotification(err));
  }, [medplum, patient]);

  useEffect(() => {
    const fetchPractitioners = async (): Promise<void> => {
      try {
        setPractitioners(await medplum.searchResources('Practitioner', { _count: '100', _sort: 'name' }));
      } catch (err) {
        showErrorNotification(err);
      }
    };
    fetchPractitioners().catch(showErrorNotification);
  }, [medplum]);

  useEffect(() => {
    setNpi(practitioner ? getIdentifier(practitioner, NPI_SYSTEM) : '');
  }, [practitioner]);

  useEffect(() => {
    const fetchConditions = async (): Promise<void> => {
      const results = await Promise.all(
        (encounter.diagnosis ?? [])
          .map((diagnosis) => diagnosis.condition?.reference?.split('/').pop())
          .filter((id): id is string => Boolean(id))
          .map((id) =>
            medplum
              .readResource('Condition', id)
              .then((condition) => condition as Condition)
              .catch(() => undefined)
          )
      );
      setConditions(results.filter((condition): condition is Condition => Boolean(condition)));
    };
    fetchConditions().catch((err) => showErrorNotification(err));
  }, [medplum, encounter]);

  const exportClaimAsCMS1500 = async (): Promise<void> => {
    if (!claim?.id || !patient?.id) {
      return;
    }

    let coverageForClaim = coverage;
    if (!coverageForClaim) {
      const coverageResults = await medplum.searchResources(
        'Coverage',
        `patient=${getReferenceString(patient)}&status=active`
      );
      if (coverageResults === undefined) {
        showErrorNotification('Failed to fetch coverage information');
        return;
      }
      if (coverageResults.length > 0) {
        coverageForClaim = coverageResults[0];
      } else {
        coverageForClaim = await createSelfPayCoverage(medplum, patient.id);
      }
    }

    const claimToExport: Claim = {
      ...claim,
      insurance: [
        {
          sequence: 1,
          focal: true,
          coverage: { reference: getReferenceString(coverageForClaim) },
        },
      ],
      diagnosis: createDiagnosisArray(conditions),
    };

    const response = await medplum.post(medplum.fhirUrl('Claim', '$export'), {
      resourceType: 'Parameters',
      parameter: [{ name: 'resource', resource: claimToExport }],
    });

    if (response.resourceType === 'Media' && response.content?.url) {
      window.open(response.content.url, '_blank');
    } else {
      showErrorNotification('Failed to download PDF');
    }
  };

  const handleEncounterChange = useDebouncedCallback(async (updatedEncounter: Encounter): Promise<void> => {
    try {
      const savedEncounter = await medplum.updateResource(updatedEncounter);
      setEncounter(savedEncounter);

      let currentPractitioner = practitioner;
      if (savedEncounter?.participant?.[0]?.individual) {
        const practitionerResult = await medplum.readReference(savedEncounter.participant[0].individual);
        currentPractitioner = practitionerResult as Practitioner;
        setPractitioner(currentPractitioner);
      }

      if (claim && currentPractitioner) {
        const providerRefNeedsUpdate = claim.provider?.reference !== getReferenceString(currentPractitioner);
        if (providerRefNeedsUpdate) {
          const updatedClaim: Claim = await medplum.updateResource({
            ...claim,
            provider: { reference: getReferenceString(currentPractitioner) },
          });
          setClaim(updatedClaim);
        }
      }
    } catch (err) {
      showErrorNotification(err);
    }
  }, SAVE_TIMEOUT_MS);

  const handlePractitionerChange = (newPractitioner: Practitioner | undefined): void => {
    if (!encounter || !newPractitioner) {
      return;
    }
    handleEncounterChange({
      ...encounter,
      participant: [{ individual: { reference: getReferenceString(newPractitioner) } }],
    });
  };

  const handleNpiBlur = async (): Promise<void> => {
    if (!practitioner?.id || npi === getIdentifier(practitioner, NPI_SYSTEM)) {
      return;
    }
    try {
      const identifiers = (practitioner.identifier ?? []).filter((identifier) => identifier.system !== NPI_SYSTEM);
      if (npi.trim()) {
        identifiers.push({ system: NPI_SYSTEM, value: npi.trim() });
      }
      const updatedPractitioner = await medplum.updateResource({ ...practitioner, identifier: identifiers });
      setPractitioner(updatedPractitioner);
    } catch (err) {
      showErrorNotification(err);
    }
  };

  const updateChargeItems = useCallback(
    async (updatedChargeItems: ChargeItem[]): Promise<void> => {
      setChargeItems(updatedChargeItems);
      if (claim?.id && encounter) {
        const updatedClaim: Claim = {
          ...claim,
          item: getCptChargeItems(updatedChargeItems, { reference: getReferenceString(encounter) }),
          total: {
            value: calculateTotalPrice(
              updatedChargeItems.filter((item) =>
                item.code?.coding?.some((coding) => coding.system === 'http://www.ama-assn.org/go/cpt')
              )
            ),
          },
        };
        setClaim(updatedClaim);
        await debouncedUpdateResource(updatedClaim);
      }
    },
    [setChargeItems, claim, encounter, setClaim, debouncedUpdateResource]
  );

  const billingSuggestion = useBillingSuggestion(encounter);
  const emCptOptions = billingSuggestion ? getEmCptOptions(billingSuggestion) : undefined;
  const primaryChargeItem = chargeItems?.find(isAppointmentChargeItem) ?? chargeItems?.[0];
  const primaryCoding =
    primaryChargeItem?.code?.coding?.find((item) => item.code) ?? primaryChargeItem?.code?.coding?.[0];
  const secondaryChargeItems = chargeItems?.filter((chargeItem) => chargeItem !== primaryChargeItem) ?? [];
  useEffect(() => {
    let cancelled = false;

    const loadFallbackPrimaryPrice = async (): Promise<void> => {
      if (primaryChargeItem || !billingSuggestion?.billingCode || !patient.id) {
        setFallbackPrimaryPrice(0);
        return;
      }

      try {
        const definition = await findActiveChargeItemDefinitionByCpt(medplum, billingSuggestion.billingCode);
        const amount = definition
          ? resolvePrice(definition, resolvePayerClass(patient, encounter.period?.start ?? new Date().toISOString()))
          : 0;
        if (!cancelled) {
          setFallbackPrimaryPrice(amount);
        }
      } catch {
        if (!cancelled) {
          setFallbackPrimaryPrice(0);
        }
      }
    };

    loadFallbackPrimaryPrice().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [billingSuggestion?.billingCode, encounter.period?.start, medplum, patient, primaryChargeItem]);

  const primaryTotal = calculateTotalPrice(primaryChargeItem ? [primaryChargeItem] : []) || fallbackPrimaryPrice;
  const totalPrice = calculateTotalPrice(chargeItems ?? []) + (primaryChargeItem ? 0 : fallbackPrimaryPrice);
  const secondaryTotal = calculateTotalPrice(secondaryChargeItems);
  const fallbackBillingCodeOptions = primaryBillingCode ? [primaryBillingCode] : [];
  const billingCodeOptions = emCptOptions
    ? [
        ...emCptOptions.codes.map((code) => ({ value: code, label: code })),
        ...(primaryBillingCode && !emCptOptions.codes.includes(primaryBillingCode)
          ? [{ value: primaryBillingCode, label: primaryBillingCode, disabled: true }]
          : []),
      ]
    : fallbackBillingCodeOptions;

  useEffect(() => {
    setPrimaryService(primaryChargeItem?.code?.text ?? primaryCoding?.display ?? billingSuggestion?.display ?? '');
    setPrimaryBillingCode(primaryCoding?.code ?? emCptOptions?.defaultCode ?? '');
    setPrimaryModifier(
      primaryChargeItem?.extension?.find((extension) => extension.url === CHARGE_ITEM_MODIFIER_URL)
        ?.valueCodeableConcept?.coding?.[0]?.code ?? ''
    );
  }, [billingSuggestion?.display, emCptOptions?.defaultCode, primaryChargeItem, primaryCoding]);

  useEffect(() => {
    const duration = getDurationFromPeriod(encounter.period?.start, encounter.period?.end);
    if (duration !== 'N/A') {
      setPrimaryDuration(duration);
    }
  }, [encounter.period?.start, encounter.period?.end]);

  const updateSecondaryChargeItems = (updatedChargeItems: ChargeItem[]): void => {
    updateChargeItems([...(primaryChargeItem ? [primaryChargeItem] : []), ...updatedChargeItems]).catch((error) =>
      showErrorNotification(error)
    );
  };

  const updatePrimaryChargeItem = async (updates: {
    service?: string;
    billingCode?: string;
    modifier?: string;
  }): Promise<void> => {
    if (!primaryChargeItem) {
      return;
    }
    const currentCoding = primaryCoding;
    const nextService = updates.service ?? primaryChargeItem.code?.text ?? currentCoding?.display ?? '';
    const nextCode = updates.billingCode ?? currentCoding?.code ?? '';
    const nextCodings = primaryChargeItem.code?.coding?.length
      ? primaryChargeItem.code.coding.map((coding, index) =>
          index === 0 ? { ...coding, code: nextCode, display: nextService } : coding
        )
      : [{ system: 'http://www.ama-assn.org/go/cpt', code: nextCode, display: nextService }];
    const otherExtensions = (primaryChargeItem.extension ?? []).filter(
      (extension) => extension.url !== CHARGE_ITEM_MODIFIER_URL
    );
    const existingModifier = primaryChargeItem.extension?.find(
      (extension) => extension.url === CHARGE_ITEM_MODIFIER_URL
    )?.valueCodeableConcept;
    const nextModifier = updates.modifier ?? existingModifier?.coding?.[0]?.code ?? existingModifier?.text ?? '';
    const nextExtensions = nextModifier
      ? [
          ...otherExtensions,
          {
            url: CHARGE_ITEM_MODIFIER_URL,
            valueCodeableConcept: {
              coding: [{ code: nextModifier, display: nextModifier }],
              text: nextModifier,
            },
          },
        ]
      : otherExtensions;
    const shouldResolvePrice = updates.billingCode !== undefined && updates.billingCode !== currentCoding?.code;
    const definition = shouldResolvePrice ? await findActiveChargeItemDefinitionByCpt(medplum, nextCode) : undefined;
    if (shouldResolvePrice && !definition?.url) {
      throw new Error(`No charge item definition found for CPT ${nextCode}`);
    }
    const savedChargeItem = await medplum.updateResource({
      ...primaryChargeItem,
      code: { ...primaryChargeItem.code, text: nextService, coding: nextCodings },
      extension: nextExtensions,
      ...(definition?.url ? { definitionCanonical: [definition.url] } : {}),
    });
    const pricedChargeItem = shouldResolvePrice
      ? await applyChargeItemDefinition(medplum, savedChargeItem)
      : savedChargeItem;
    await updateChargeItems([pricedChargeItem, ...secondaryChargeItems]);
  };

  const handleDurationChange = (value: string): void => {
    const numericValue = value.replace(/[^0-9]/g, '');
    setPrimaryDuration(numericValue);

    const minutes = Number.parseInt(numericValue, 10);
    if (!Number.isFinite(minutes)) {
      return;
    }
    const start = encounter.period?.start ?? new Date().toISOString();
    handleEncounterChange({
      ...encounter,
      period: { ...encounter.period, end: new Date(new Date(start).getTime() + minutes * 60000).toISOString() },
    });
  };

  return (
    <Card withBorder radius="lg" p="lg">
      <Title order={3} mb="lg">
        Billing
      </Title>

      <Grid gutter="xl">
        <Grid.Col>
          <Grid columns={12}>
            <Grid.Col span={8}>
              <Select
                label="Rendering Provider"
                data={practitionerOptions}
                value={practitioner?.id ?? null}
                placeholder="Select practitioner"
                disabled={isLocked}
                onChange={(value) => {
                  const selectedPractitioner = practitioners.find((item) => item.id === value);
                  handlePractitionerChange(selectedPractitioner);
                }}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <TextInput
                label="NPI"
                value={npi}
                placeholder="Enter NPI"
                disabled={isLocked || !practitioner}
                onChange={(event) => setNpi(event.currentTarget.value)}
                onBlur={() => handleNpiBlur().catch(() => undefined)}
              />
            </Grid.Col>
          </Grid>
        </Grid.Col>

        <Grid.Col>
          <Text fw={600} size="lg" mb="sm">
            Primary Billed Service
          </Text>
          <Grid gutter="md" mb="lg">
            <Grid.Col span={{ base: 12, md: 5 }}>
              <TextInput
                label="Billed Service"
                value={primaryService}
                placeholder="Billed service"
                disabled={isLocked}
                onChange={(event) => setPrimaryService(event.currentTarget.value)}
                onBlur={() => updatePrimaryChargeItem({ service: primaryService }).catch(showErrorNotification)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
              <TextInput
                label="Duration (minutes)"
                value={primaryDuration === 'N/A' ? '' : primaryDuration}
                placeholder="Enter minutes"
                inputMode="numeric"
                pattern="[0-9]*"
                disabled={isLocked}
                onChange={(event) => handleDurationChange(event.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4, md: 2 }}>
              <Select
                label="Billing Code"
                data={billingCodeOptions}
                value={primaryBillingCode}
                placeholder="CPT"
                disabled={isLocked}
                onChange={(value) => {
                  const nextCode = value ?? '';
                  setPrimaryBillingCode(nextCode);
                  updatePrimaryChargeItem({ billingCode: nextCode }).catch(showErrorNotification);
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4, md: 2 }}>
              <TextInput
                label="Modifiers"
                value={primaryModifier}
                placeholder="Modifier"
                disabled={isLocked}
                onChange={(event) => setPrimaryModifier(event.currentTarget.value)}
                onBlur={() => updatePrimaryChargeItem({ modifier: primaryModifier }).catch(showErrorNotification)}
              />
            </Grid.Col>
          </Grid>
          {chargeItems && (
            <ChargeItemList
              patient={patient}
              encounter={encounter}
              chargeItems={secondaryChargeItems}
              readOnly={isLocked}
              updateChargeItems={updateSecondaryChargeItems}
              heading="Secondary Billing Codes"
              addLabel="Add Secondary Code"
              secondary
            />
          )}
        </Grid.Col>
      </Grid>

      <Divider my="lg" />

      <Flex justify="space-between" align="center">
        <Text fw={600} size="md">
          Total Charge:
        </Text>
        <Text fw={700} size="xl">
          ${totalPrice.toFixed(2)}
        </Text>
      </Flex>

      <Flex justify="flex-end" gap="xl" mt="sm">
        <Text c="dimmed" size="sm">
          Primary service ${primaryTotal.toFixed(2)}
        </Text>
        <Text c="dimmed" size="sm">
          Secondary codes ${secondaryTotal.toFixed(2)}
        </Text>
      </Flex>

      {claim && (
        <Flex justify="space-between" align="center" mt="md">
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <Button variant="outline" size="sm" leftSection={<IconDownload size={16} />}>
                Export Claim
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Export Options</Menu.Label>
              <Menu.Item
                leftSection={<IconFileText size={14} />}
                onClick={async () => {
                  await exportClaimAsCMS1500();
                }}
              >
                CMS 1500 Form
              </Menu.Item>
              <Menu.Item
                leftSection={<IconFileText size={14} />}
                onClick={() => {
                  showNotification({
                    title: 'EDI X12',
                    message: 'Please contact sales to enable EDI X12 export',
                    color: 'blue',
                  });
                }}
              >
                EDI X12
              </Menu.Item>
              <Menu.Item
                leftSection={<IconFileText size={14} />}
                onClick={() => {
                  showNotification({
                    title: 'NUCC Crosswalk',
                    message: 'Please contact sales to enable NUCC Crosswalk export',
                    color: 'blue',
                  });
                }}
              >
                NUCC Crosswalk CSV
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <Button variant="outline" size="sm" leftSection={<IconSend size={16} />}>
            Request to connect a billing service
          </Button>
        </Flex>
      )}
    </Card>
  );
};

function useBillingSuggestion(encounter: Encounter):
  | {
      display: string;
      billingCode: string;
      billingModifier: string;
      billingCodes: string[];
      estimateCode: 'intake' | 'check-in' | 'follow-up' | '';
    }
  | undefined {
  const { appointmentTypes } = useAppointmentTypes();

  const encounterTypeCode = encounter.type?.[0]?.coding?.find((c) => c.system === APPOINTMENT_TYPE_SYSTEM)?.code;

  if (!encounterTypeCode) {
    return undefined;
  }

  const apptType = appointmentTypes.find((t) => t.coding.code === encounterTypeCode);
  if (!apptType) {
    return undefined;
  }

  return {
    display: apptType.display,
    billingCode: apptType.billingCode,
    billingModifier: apptType.billingModifier,
    billingCodes: apptType.billingCodes ?? (apptType.billingCode ? [apptType.billingCode] : []),
    estimateCode: apptType.estimateCode ?? '',
  };
}

const createDiagnosisArray = (conditions: Condition[]): ClaimDiagnosis[] => {
  return conditions.map((condition, index) => {
    const icd10Coding = condition.code?.coding?.find((c) => c.system === `${HTTP_HL7_ORG}/fhir/sid/icd-10-cm`);
    return {
      diagnosisCodeableConcept: {
        coding: icd10Coding ? [{ ...icd10Coding, system: `${HTTP_HL7_ORG}/fhir/sid/icd-10` }] : [],
      },
      sequence: index + 1,
      type: [{ coding: [{ code: index === 0 ? 'principal' : 'secondary' }] }],
    };
  });
};
